import {ADMIN_TABS} from 'utils/constants';

/**
 * Builds a flat, searchable index of dashboard destinations from ADMIN_TABS.
 *
 * Only destinations that can be reached WITHOUT a dynamic id (e.g. :id) are
 * indexed. Because the index is derived straight from ADMIN_TABS (the same
 * source the sidebar renders from), every result is guaranteed to point at a
 * tab/sub-tab that actually exists and navigates exactly like the sidebar link.
 *
 * Each entry:
 *  - id:      stable unique key
 *  - label:   the tab / sub-tab name shown to the admin
 *  - parent:  the group name (for sub-tabs) or null for top-level tabs
 *  - to:      the raw `to` value from ADMIN_TABS
 *  - path:    the resolved absolute route (mirrors the sidebar CustomLink)
 *  - keywords: lowercased text used for matching
 */

const DASHBOARD_BASE = '/dashboard';

// Resolve a nav `to` value to the same absolute path the sidebar link produces.
const resolvePath = to => {
  if (to === '' || to == null) return DASHBOARD_BASE;
  const clean = String(to).replace(/^\/+/, '');
  return `${DASHBOARD_BASE}/${clean}`;
};

const buildKeywords = (label, parent) =>
  [label, parent].filter(Boolean).join(' ').toLowerCase();

// A few real, id-less destinations that live in the header dropdown rather than
// the sidebar, so admins can jump to them from search too.
const EXTRA_DESTINATIONS = [
  {
    id: 'extra:admin-settings',
    label: 'Admin Settings',
    parent: 'Settings',
    to: 'admin-settings',
    path: '/dashboard/admin-settings',
    icon: ADMIN_TABS.Settings?.icon,
  },
  {
    id: 'extra:referral-partners',
    label: 'Referral Partners',
    parent: 'Settings',
    to: 'referral-partners',
    path: '/dashboard/referral-partners',
    icon: ADMIN_TABS.Settings?.icon,
  },
].map(item => ({...item, keywords: buildKeywords(item.label, item.parent)}));

export const buildDashboardSearchIndex = (tabs = ADMIN_TABS) => {
  const index = [];

  Object.keys(tabs).forEach(key => {
    const tab = tabs[key];
    if (!tab || tab.hideOnProd) return;

    if (tab.inner) {
      // Group tab: index its sub-tabs, tagging them with the group name.
      Object.keys(tab.inner).forEach(innerKey => {
        const inner = tab.inner[innerKey];
        if (!inner || inner.hideOnProd) return;
        // Only index destinations that resolve without a dynamic id.
        if (inner.to == null || String(inner.to).includes(':')) return;

        index.push({
          id: `${key}:${innerKey}`,
          label: inner.name,
          parent: tab.name,
          to: inner.to,
          path: resolvePath(inner.to),
          icon: inner.icon,
          keywords: buildKeywords(inner.name, tab.name),
        });
      });
      return;
    }

    // Top-level, directly navigable tab.
    if (tab.to == null || String(tab.to).includes(':')) return;

    index.push({
      id: key,
      label: tab.name,
      parent: null,
      to: tab.to,
      path: resolvePath(tab.to),
      icon: tab.icon,
      keywords: buildKeywords(tab.name, null),
    });
  });

  return [...index, ...EXTRA_DESTINATIONS];
};

/**
 * Resolves the index entry that best describes a given pathname, so the global
 * "Add to history" button can label whatever page the admin is on.
 * Prefers an exact route match, then the longest prefix match (segment-aware),
 * so e.g. /dashboard/wallet/view-details/x still resolves to "Wallet".
 */
export const resolveEntryForPath = (index, pathname) => {
  if (!pathname) return null;
  const clean = pathname.replace(/\/+$/, '') || '/dashboard';

  const exact = index.find(entry => entry.path === clean);
  if (exact) return exact;

  let best = null;
  index.forEach(entry => {
    // Skip the bare /dashboard (Overview) for prefix matching — it would match
    // every route. It can still win via the exact check above.
    if (entry.path === '/dashboard') return;
    if (clean.startsWith(`${entry.path}/`)) {
      if (!best || entry.path.length > best.path.length) best = entry;
    }
  });
  return best;
};

/**
 * Filters and ranks the index against a query string.
 * A result matches when every whitespace-separated token in the query is a
 * substring of the entry's keywords. Results are ranked so that stronger
 * matches (exact / prefix on the label) surface first.
 */
export const searchDashboard = (index, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];

  const tokens = q.split(/\s+/).filter(Boolean);

  return index
    .map(entry => {
      const label = entry.label.toLowerCase();
      const matchesAll = tokens.every(token => entry.keywords.includes(token));
      if (!matchesAll) return null;

      let score = 0;
      if (label === q) score = 100;
      else if (label.startsWith(q)) score = 75;
      else if (label.includes(q)) score = 50;
      else score = 25; // matched only via parent / partial tokens

      return {entry, score};
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label))
    .map(result => result.entry);
};
